import React, { useMemo, useEffect, useCallback, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import {
    BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
    CartesianGrid, Cell
} from 'recharts';
import { Spinner } from 'react-bootstrap';
import { COLOUR_PALETTE } from '../utils/costingHelpers';

// Renders tooltip via portal so it escapes all overflow:hidden ancestors
function PortalTooltip({ active, payload, label, coordinate, viewBox, chartRef }) {
    if (!active || !payload || payload.length === 0) return null;

    const total = payload.reduce((sum, p) => sum + (p.value || 0), 0);
    const sorted = [...payload].filter(p => p.value > 0).sort((a, b) => b.value - a.value);

    const chartEl = chartRef?.current;
    if (!chartEl) return null;

    const chartRect = chartEl.getBoundingClientRect();
    const left = chartRect.left + (coordinate?.x ?? 0);
    const top = chartRect.top + (viewBox?.y ?? 0) - 8;

    return createPortal(
        <div style={{
            position: 'fixed',
            left,
            top,
            transform: 'translate(-50%, -100%)',
            background: 'rgba(7, 1, 28, 0.95)',
            border: '1px solid rgba(255,255,255,0.15)',
            borderRadius: 8,
            padding: '10px 14px',
            minWidth: 220,
            zIndex: 9999,
            pointerEvents: 'none',
        }}>
            <div style={{ color: 'white', fontWeight: 600, fontSize: 13, marginBottom: 8, borderBottom: '1px solid rgba(255,255,255,0.1)', paddingBottom: 6 }}>
                {label} — £{total.toLocaleString('en-GB')}
            </div>
            {sorted.map((entry, i) => {
                const pct = total > 0 ? ((entry.value / total) * 100).toFixed(1) : '0';
                return (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '2px 0', fontSize: 11, color: 'white' }}>
                        <span style={{ width: 10, height: 10, borderRadius: 2, background: entry.color, flexShrink: 0 }} />
                        <span style={{ flex: 1, opacity: 0.9 }}>{entry.name}</span>
                        <span style={{ fontWeight: 500, minWidth: 65, textAlign: 'right' }}>£{entry.value.toLocaleString('en-GB')}</span>
                        <span style={{ opacity: 0.5, minWidth: 40, textAlign: 'right' }}>{pct}%</span>
                    </div>
                );
            })}
        </div>,
        document.body
    );
}

PortalTooltip.propTypes = {
    active: PropTypes.bool,
    payload: PropTypes.array,
    label: PropTypes.string,
    coordinate: PropTypes.object,
    viewBox: PropTypes.object,
    chartRef: PropTypes.object,
};

function formatCurrency(value) {
    return `£${value.toLocaleString('en-GB')}`;
}

function RoleCostChart({
    data,
    onBarClick,
    chartSelection,
    periodRange,
    onPeriodRangeChange,
    dataLoaded,
    containerHeight,
    containerWidth
}) {
    const chartRef = useRef(null);

    const uniqueRoles = useMemo(() => {
        if (!data || data.length === 0) return [];
        const roleTotals = {};
        data.forEach(row => {
            const role = row.roleName || '';
            const nett = parseFloat(row.nett) || 0;
            if (role) roleTotals[role] = (roleTotals[role] || 0) + nett;
        });
        return Object.entries(roleTotals)
            .sort(([, a], [, b]) => b - a)
            .map(([name], i) => ({
                name,
                colour: COLOUR_PALETTE[i % COLOUR_PALETTE.length]
            }));
    }, [data]);

    const availablePeriods = useMemo(() => {
        if (!data || data.length === 0) return [];
        const periodsSet = new Set();
        data.forEach(row => {
            if (row.periodName) periodsSet.add(row.periodName);
        });
        return Array.from(periodsSet).sort((a, b) => {
            const [yearA, numA] = a.split(' ');
            const [yearB, numB] = b.split(' ');
            if (yearA !== yearB) return parseInt(yearA) - parseInt(yearB);
            return parseInt(numA) - parseInt(numB);
        });
    }, [data]);

    useEffect(() => {
        if (availablePeriods.length > 0) {
            if (!periodRange || periodRange[0] === null) {
                onPeriodRangeChange?.([0, availablePeriods.length - 1]);
            }
        }
    }, [availablePeriods.length, periodRange, onPeriodRangeChange]);

    const selectedPeriods = useMemo(() => {
        if (!periodRange || periodRange[0] === null || availablePeriods.length === 0) return [];
        const [startIdx, endIdx] = periodRange;
        return availablePeriods.slice(startIdx, endIdx + 1);
    }, [periodRange, availablePeriods]);

    const chartData = useMemo(() => {
        if (!data || data.length === 0) return [];
        let filteredData = data;
        if (selectedPeriods.length > 0) {
            filteredData = data.filter(row => selectedPeriods.includes(row.periodName));
        }
        const byGroup = {};
        filteredData.forEach(row => {
            let groupKey = 'Unknown';
            if (row.weekEnding) {
                groupKey = new Date(row.weekEnding).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit' });
            }
            const roleName = row.roleName || '';
            const nett = parseFloat(row.nett) || 0;
            if (!byGroup[groupKey]) {
                byGroup[groupKey] = { group: groupKey, _weekEnding: row.weekEnding };
                uniqueRoles.forEach(r => { byGroup[groupKey][r.name] = 0; });
            }
            if (roleName && byGroup[groupKey].hasOwnProperty(roleName)) {
                byGroup[groupKey][roleName] += nett;
            }
        });

        return Object.values(byGroup)
            .filter(entry => {
                const total = uniqueRoles.reduce((sum, r) => sum + (entry[r.name] || 0), 0);
                return total > 0;
            })
            .sort((a, b) => {
                const dateA = a._weekEnding ? new Date(a._weekEnding) : new Date(0);
                const dateB = b._weekEnding ? new Date(b._weekEnding) : new Date(0);
                return dateA - dateB;
            });
    }, [data, selectedPeriods, uniqueRoles]);

    const handleBarClick = useCallback((roleName) => (barData, index) => {
        if (!onBarClick) return;
        const clickedGroup = chartData[index];
        onBarClick({
            group: clickedGroup.group,
            role: roleName,
            weekEnding: clickedGroup._weekEnding
        });
    }, [onBarClick, chartData]);

    function isBarSelected(groupKey, role) {
        if (!chartSelection) return false;
        return chartSelection.group === groupKey && chartSelection.role === role;
    }

    const trackRef = useRef(null);
    const draggingRef = useRef(null);
    const [localRange, setLocalRange] = useState(null);

    const maxIdx = availablePeriods.length - 1;
    const commitMin = periodRange?.[0] ?? 0;
    const commitMax = periodRange?.[1] ?? maxIdx;
    const displayMin = localRange ? localRange[0] : commitMin;
    const displayMax = localRange ? localRange[1] : commitMax;

    const posToIndex = useCallback((clientX) => {
        const track = trackRef.current;
        if (!track) return 0;
        const rect = track.getBoundingClientRect();
        const ratio = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
        return Math.round(ratio * maxIdx);
    }, [maxIdx]);

    const onPointerDown = useCallback((handle) => (e) => {
        e.preventDefault();
        draggingRef.current = handle;
        document.body.style.userSelect = 'none';
    }, []);

    useEffect(() => {
        const onPointerMove = (e) => {
            if (!draggingRef.current) return;
            const idx = posToIndex(e.clientX ?? e.touches?.[0]?.clientX);
            setLocalRange(prev => {
                const cur = prev || [commitMin, commitMax];
                if (draggingRef.current === 'min') {
                    return [Math.min(idx, cur[1]), cur[1]];
                }
                return [cur[0], Math.max(idx, cur[0])];
            });
        };
        const onPointerUp = () => {
            if (!draggingRef.current) return;
            draggingRef.current = null;
            document.body.style.userSelect = '';
            setLocalRange(prev => {
                if (prev) onPeriodRangeChange?.(prev);
                return null;
            });
        };
        window.addEventListener('pointermove', onPointerMove);
        window.addEventListener('pointerup', onPointerUp);
        window.addEventListener('touchmove', onPointerMove, { passive: false });
        window.addEventListener('touchend', onPointerUp);
        return () => {
            window.removeEventListener('pointermove', onPointerMove);
            window.removeEventListener('pointerup', onPointerUp);
            window.removeEventListener('touchmove', onPointerMove);
            window.removeEventListener('touchend', onPointerUp);
        };
    }, [commitMin, commitMax, posToIndex, onPeriodRangeChange]);

    const leftPct = maxIdx > 0 ? (displayMin / maxIdx) * 100 : 0;
    const rightPct = maxIdx > 0 ? (displayMax / maxIdx) * 100 : 100;

    const minPeriodLabel = availablePeriods[displayMin] || '';
    const maxPeriodLabel = availablePeriods[displayMax] || '';

    const legend = useMemo(() => (
        <div style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '4px 8px',
            padding: '4px 8px',
            overflowY: 'auto',
            marginBottom: 4,
        }}>
            {uniqueRoles.map(role => (
                <div key={role.name} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 10, color: 'white', opacity: 0.85, whiteSpace: 'nowrap' }}>
                    <span style={{ width: 8, height: 8, borderRadius: 2, background: role.colour, flexShrink: 0 }} />
                    {role.name}
                </div>
            ))}
        </div>
    ), [uniqueRoles]);

    if (!dataLoaded) {
        return (
            <div className="inline-loading">
                <Spinner animation="border" size="sm" />
            </div>
        );
    }

    if (chartData.length === 0) {
        return (
            <div className="chart-empty"><p>No data available for chart</p></div>
        );
    }

    return (
        <div className="role-cost-chart">
            {availablePeriods.length > 0 && (
                <div className="chart-controls">
                    <div className="period-range-slider">
                        <div className="range-labels">
                            <span className="range-label">{minPeriodLabel}</span>
                            <span className="range-separator">to</span>
                            <span className="range-label">{maxPeriodLabel}</span>
                        </div>
                        <div className="slider-container" ref={trackRef}>
                            <div className="slider-track" />
                            <div className="slider-fill" style={{ left: `${leftPct}%`, width: `${rightPct - leftPct}%` }} />
                            <div className="slider-thumb slider-thumb--min" style={{ left: `${leftPct}%` }} onPointerDown={onPointerDown('min')} />
                            <div className="slider-thumb slider-thumb--max" style={{ left: `${rightPct}%` }} onPointerDown={onPointerDown('max')} />
                        </div>
                    </div>
                </div>
            )}

            {legend}

            <div ref={chartRef} className="chart-area" style={containerHeight ? { height: containerHeight, width: containerWidth || '100%', flex: 'none' } : undefined}>
                <ResponsiveContainer width={containerWidth || "100%"} height={containerHeight || "100%"}>
                    <BarChart
                        data={chartData}
                        margin={{ top: 8, right: 30, left: 20, bottom: 5 }}
                        barCategoryGap="25%"
                        maxBarSize={120}
                    >
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.08)" vertical={false} />
                        <XAxis
                            dataKey="group"
                            tick={{ fill: 'white', fontSize: 11 }}
                            axisLine={{ stroke: 'rgba(255,255,255,0.15)' }}
                            tickLine={false}
                        />
                        <YAxis
                            tick={{ fill: 'white', fontSize: 11 }}
                            tickFormatter={formatCurrency}
                            domain={[0, 'auto']}
                            axisLine={false}
                            tickLine={false}
                        />
                        <Tooltip content={<PortalTooltip chartRef={chartRef} />} cursor={false} wrapperStyle={{ visibility: 'hidden', width: 0, height: 0 }} />
                        {uniqueRoles.map((role) => (
                            <Bar
                                key={role.name}
                                dataKey={role.name}
                                stackId="a"
                                fill={role.colour}
                                name={role.name}
                                onClick={handleBarClick(role.name)}
                                cursor="pointer"
                                activeBar={false}
                                radius={0}
                            >
                                {chartData.map((entry, index) => {
                                    const selected = isBarSelected(entry.group, role.name);
                                    return (
                                        <Cell
                                            key={`${role.name}-${index}`}
                                            fill={role.colour}
                                            fillOpacity={selected ? 1 : 0.85}
                                            stroke={selected ? '#fff' : 'none'}
                                            strokeWidth={selected ? 2 : 0}
                                        />
                                    );
                                })}
                            </Bar>
                        ))}
                    </BarChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
}

RoleCostChart.propTypes = {
    data: PropTypes.array,
    onBarClick: PropTypes.func,
    chartSelection: PropTypes.object,
    periodRange: PropTypes.array,
    onPeriodRangeChange: PropTypes.func,
    dataLoaded: PropTypes.bool,
    containerHeight: PropTypes.number,
    containerWidth: PropTypes.number
};

export default RoleCostChart;
